export interface UploadOptions {
  url: string;
  formData: FormData;
  onProgress?: (progress: number) => void;
}

export interface UploadResult<T> {
  data: T;
  status: number;
}

export function uploadWithProgress<T>({
  url,
  formData,
  onProgress,
}: UploadOptions): Promise<UploadResult<T>> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable && onProgress) {
        const progress = Math.round((event.loaded / event.total) * 100);
        onProgress(progress);
      }
    });

    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText) as T;
          resolve({ data, status: xhr.status });
        } catch {
          reject(new Error("Failed to parse response"));
        }
      } else if (xhr.status === 401) {
        // Let the auth handler deal with this
        window.location.reload();
      } else if (xhr.status === 403) {
        reject(new Error("You don't have permission to upload photos"));
      } else {
        try {
          const error = JSON.parse(xhr.responseText);
          reject(new Error(error.detail || "Upload failed"));
        } catch {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      }
    });

    xhr.addEventListener("error", () => {
      reject(new Error("Network error during upload"));
    });

    xhr.addEventListener("abort", () => {
      reject(new Error("Upload cancelled"));
    });

    xhr.open("POST", url);
    xhr.withCredentials = true; // Include cookies for auth
    xhr.send(formData);
  });
}
